import React, { useEffect, useState } from 'react';
import { Modal } from "react-bootstrap";
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { storeActivityIndicator, storeAreYouSureModalCallback } from '../../../../redux/reducers/authSlice';
import { CALISMA, durumRengi, OtomatikFaturaCalismaDetayi, OtomatikFaturaService, tl } from '../../../../services/OtomatikFaturaService';

interface Props {
    calismaId: string | undefined
    onClose: () => void
    onDegisti: () => void
}

const BELGE_RENGI: { [durum: number]: string } = { 0: 'info', 1: 'primary', 2: 'success', 3: 'success', 4: 'danger', 5: 'secondary' };
const SEVIYE_RENGI: { [seviye: number]: string } = { 1: 'text-gray-700', 2: 'text-warning', 3: 'text-danger' };

/**
 * Çalışma ayrıntısı: oluşan faturalar (durum ve hata mesajıyla), adım adım kayıt satırları ve işlemler
 * (hatalıları yeniden dene, onay mailini yeniden gönder, onayla / reddet — yalnız kuralın onaylayıcısına).
 */
const CalismaDetayModal = ({ calismaId, onClose, onDegisti }: Props) => {
    const dispatch = useDispatch();
    const [detay, setDetay] = useState<OtomatikFaturaCalismaDetayi | undefined>(undefined);
    const [retNotu, setRetNotu] = useState<string>('');

    useEffect(() => {
        setDetay(undefined);
        setRetNotu('');

        if (calismaId)
            getir();
    }, [calismaId])

    const getir = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await OtomatikFaturaService.calismaDetayi(calismaId!);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setDetay(response.data);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const islem = async (cagri: () => Promise<any>, basari: string) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await cagri();

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            toast.success(typeof response.data == 'string' ? response.data : basari);
            onDegisti();
            await getir();
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const c = detay?.calisma;
    const yenidenDenenebilir = !!c && [CALISMA.HATALI, CALISMA.KISMEN_HATALI, CALISMA.REDDEDILDI].includes(c.durum);

    return (
        <Modal show={!!calismaId} onHide={onClose} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {c ? <span>{c.kuralAdi} · {c.donemAdi} <span className={`badge badge-light-${durumRengi(c.durum)} ms-2 fs-7`}>{c.durumAdi}</span></span> : 'Çalışma'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    c && detay && (
                        <React.Fragment>
                            <div className="d-flex flex-wrap gap-8 mb-6 fs-7 text-gray-700">
                                <div><div className="text-muted">Tetikleyen</div>{c.tetikleyenAdi} · {c.planlananZamanStr}</div>
                                <div><div className="text-muted">Fatura tarihi</div>{c.faturaTarihiStr}</div>
                                <div><div className="text-muted">Gönderim</div>{c.gonderimAdi}</div>
                                <div><div className="text-muted">Onay</div>{c.onayGerekli ? (c.onaylayanAdi ? `${c.onaylayanAdi} · ${c.onayZamaniStr} · ${c.onayKanali}` : `${c.onaylayiciAdi ?? ''} bekleniyor`) : 'Onaysız'}</div>
                                <div><div className="text-muted">Hatırlatma</div>{c.hatirlatmaSayisi}</div>
                                <div><div className="text-muted">Toplam (₺)</div><b>{tl(c.toplam)}</b></div>
                            </div>

                            <h5 className="mb-3">Faturalar</h5>
                            <div className='table-responsive mb-6'>
                                <table className='table align-middle table-row-dashed ebbe-compact-table'>
                                    <thead>
                                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                                            <th>Müşteri</th>
                                            <th>PO No</th>
                                            <th>Açıklama</th>
                                            <th>Sipariş</th>
                                            <th>Fatura</th>
                                            <th className="text-end">Matrah</th>
                                            <th className="text-end">KDV</th>
                                            <th className="text-end">Tevkifat</th>
                                            <th className="text-end">Toplam</th>
                                            <th>Durum</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-700 fw-semibold'>
                                        {
                                            detay.belgeler.length == 0 && <tr><td className="text-center" colSpan={99}>Fatura yok.</td></tr>
                                        }
                                        {
                                            detay.belgeler.map(b => (
                                                <React.Fragment key={b.id}>
                                                    <tr>
                                                        <td>{b.musteriAdi}</td>
                                                        <td>{b.poNo}</td>
                                                        <td>{b.aciklama}</td>
                                                        <td>{b.ambarRefNo ?? (b.siparisSayisi > 0 ? `${b.siparisSayisi} sipariş` : '')}</td>
                                                        <td>
                                                            {
                                                                b.invoiceId
                                                                    ? <a href={'/saleinvoice/listadd?id=' + b.invoiceId} target="_blank" rel="noreferrer">{b.faturaNo ?? b.faturaRefNo}</a>
                                                                    : '—'
                                                            }
                                                        </td>
                                                        <td className="text-end">{tl(b.matrah)}</td>
                                                        <td className="text-end">{tl(b.kdv)}</td>
                                                        <td className="text-end">{b.tevkifat ? tl(b.tevkifat) : '—'}</td>
                                                        <td className="text-end">{tl(b.toplam)} {b.paraBirimi && b.paraBirimi != 'TRY' ? b.paraBirimi : ''}</td>
                                                        <td><span className={`badge badge-light-${BELGE_RENGI[b.durum] ?? 'info'}`}>{b.durumAdi}</span></td>
                                                    </tr>
                                                    {
                                                        b.mesaj && (
                                                            <tr>
                                                                <td colSpan={99} className={'fs-7 pt-0 ' + (b.durum == 4 ? 'text-danger' : 'text-muted')}>{b.mesaj}</td>
                                                            </tr>
                                                        )
                                                    }
                                                </React.Fragment>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>

                            <h5 className="mb-3">Kayıt</h5>
                            <div className="bg-light rounded p-4 fs-7" style={{ fontFamily: 'monospace', maxHeight: 260, overflowY: 'auto' }}>
                                {detay.kayitlar.map((k, i) => <div key={i} className={SEVIYE_RENGI[k.seviye] ?? ''}>[{k.zaman}] {k.mesaj}</div>)}
                            </div>

                            {
                                detay.onaylayabilirMi && (
                                    <div className="border border-warning rounded p-4 mt-6 bg-light-warning">
                                        <div className="fw-bold mb-2">Onayınız bekleniyor</div>
                                        <textarea className="form-control form-control-sm mb-3" rows={2} maxLength={500} placeholder="Ret sebebi (isteğe bağlı)" value={retNotu} onChange={e => setRetNotu(e.target.value)} />
                                        <div className="d-flex gap-3">
                                            <button className="btn btn-sm btn-success" onClick={() => dispatch(storeAreYouSureModalCallback(() => islem(() => OtomatikFaturaService.onay(c.otomatikFaturaCalismasiId, true), 'Onaylandı.')))}>Onayla</button>
                                            <button className="btn btn-sm btn-danger" onClick={() => dispatch(storeAreYouSureModalCallback(() => islem(() => OtomatikFaturaService.onay(c.otomatikFaturaCalismasiId, false, retNotu), 'Reddedildi.')))}>Reddet</button>
                                        </div>
                                    </div>
                                )
                            }
                        </React.Fragment>
                    )
                }
            </Modal.Body>
            <Modal.Footer>
                {
                    c?.durum == CALISMA.ONAY_BEKLIYOR && (
                        <button className="btn btn-sm btn-light-warning" onClick={() => islem(() => OtomatikFaturaService.onayMailiniYenidenGonder(c.otomatikFaturaCalismasiId), 'Onay maili yeniden gönderiliyor (yeni bağlantıyla).')}>Onay mailini yeniden gönder</button>
                    )
                }
                {
                    yenidenDenenebilir && (
                        <button className="btn btn-sm btn-light-primary" onClick={() => dispatch(storeAreYouSureModalCallback(() => islem(() => OtomatikFaturaService.hatalilariYenidenDene(c!.otomatikFaturaCalismasiId), 'Yeniden deneniyor.')))}>
                            {c!.durum == CALISMA.REDDEDILDI ? 'Yeniden çalıştır (taslakları güncelle, onay iste)' : 'Hatalıları yeniden dene'}
                        </button>
                    )
                }
                <button className="btn btn-sm btn-light" onClick={getir}>Yenile</button>
                <button className="btn btn-sm btn-light" onClick={onClose}>Kapat</button>
            </Modal.Footer>
        </Modal>
    )
}

export default CalismaDetayModal;
